import React, {useEffect, useState} from "react";
import {
    Button, Dialog, DialogActions,
    DialogContent, DialogTitle, Grid, Typography,
} from "@mui/material";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useTranslation} from "react-i18next";
import {useRecoilValue} from "recoil";
import dayjs, {Dayjs} from "dayjs";
import _ from "underscore";
import {FormDateTimePicker, FormTextField} from "../../common/form-fields-wrappers";
import {ICustomerDataForm, IRecoveryRaReasonForm} from "../../models/models";
import {REASON_RECOVERY_RA_FORM_INITIAL_VALUES, REASON_RECOVERY_RA_VALIDATION_SCHEMA} from "../customer-data/config";
import {bookingDataValueState, recoveryRaInfoState} from "../state";
import {FINANCIAL_PAYMENT_TIMING, SegmentCode} from "../../config";

const RecoveryDialogReason =  (props: any) => {
    const {onClose = null, onSave, backdropClick=true, dialogTitle='Title', open} = props;
    const {t} = useTranslation(['network', 'translation']);
    const bookingDataValue = useRecoilValue(bookingDataValueState);
    const recoveryRaInfo = useRecoilValue(recoveryRaInfoState);

    const [minDate, setMinDate] = useState<Dayjs>();
    const [maxDate, setMaxDate] = useState<Dayjs>();

    const reasonRecoveryForm = useForm<IRecoveryRaReasonForm>({
        defaultValues: REASON_RECOVERY_RA_FORM_INITIAL_VALUES,
        resolver: yupResolver(REASON_RECOVERY_RA_VALIDATION_SCHEMA),
        context: {
            minDate,
            maxDate
        }
    })

    const handleClose = (event: React.SyntheticEvent, reason: string) => {
        event.stopPropagation();

        if (reason === 'backdropClick' && !backdropClick) {
            return;
        }

        if (!!onClose) {
            onClose();
        }
    };

    const handleSave = (data: any) => {
        if (!!onSave) {
            onSave(data);
        }
    }

    useEffect(() => {
        if (!_.isEmpty(bookingDataValue)) {
            const now = dayjs();
            //tutti i segmenti possono essere presi in anticipo lo stesso gg del pickup previsto
            setMinDate(dayjs(bookingDataValue.bookingDetail.pickupDate).set('hour', 0).set('minute', 0).set('second', 0));
            //tutti i segmenti possono essere preso in ritardo lo stesso gg del pickup previsto
            setMaxDate(now.isBefore(dayjs(bookingDataValue.bookingDetail.pickupDate).set('hour', 23).set('minute', 59).set('second', 59)) ? now : dayjs(bookingDataValue.bookingDetail.pickupDate).set('hour', 23).set('minute', 59).set('second', 59));

            //solo i segmenti retail prepaid, tour operator e subscription possono essere presi in ritardo fino al giorno del dropoff
            if (!!bookingDataValue.financialInfo.segment && (
                (bookingDataValue.financialInfo.segment === SegmentCode.retail && bookingDataValue.financialInfo.paymentTiming === FINANCIAL_PAYMENT_TIMING.prepaid) || 
                bookingDataValue.financialInfo.segment === SegmentCode.tourOperator ||
                bookingDataValue.financialInfo.segment === SegmentCode.subscription ||
                !!bookingDataValue.bookingDetail?.flagSubstitution
            )) {
                setMaxDate(now.isBefore(bookingDataValue.bookingDetail.dropoffDate) ? now : dayjs(bookingDataValue.bookingDetail.dropoffDate));
            }
        }
    }, [bookingDataValue]);

    useEffect(() => {
        if (open) {
            if (recoveryRaInfo) {
                // Pre-popola con i dati esistenti della recovery note
                reasonRecoveryForm.reset({
                    reason: recoveryRaInfo.reason || '',
                    effectivePickupDate: recoveryRaInfo.effectivePickupDate || null
                });
            } else {
                // Nuova booking senza recovery note: form vuoto
                reasonRecoveryForm.reset(REASON_RECOVERY_RA_FORM_INITIAL_VALUES);
            }
        }
    }, [open, recoveryRaInfo]);

    return (
        <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth sx={{minWidth: "400px"}}>
            <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <Typography variant={"h6"}>
                    {t(dialogTitle)}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <FormProvider {...reasonRecoveryForm}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12}>
                            <FormTextField<IRecoveryRaReasonForm>
                                name={'reason'}
                                control={reasonRecoveryForm.control}
                                label={t("recoveryRaReason.form.label.reason")}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>
                        <Grid item xs={4} sx={{minWidth: 300}}>
                            <FormDateTimePicker<ICustomerDataForm>
                                name={'effectivePickupDate'}
                                control={reasonRecoveryForm.control}
                                label={t("recoveryRaReason.form.label.effectivePickupDate")}
                                minDateTime={minDate}
                                maxDateTime={maxDate}
                            />
                        </Grid>
                    </Grid>
                </FormProvider>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={reasonRecoveryForm.handleSubmit(handleSave)}
                    disabled={false}
                >
                    {t('recoveryRaReason.button.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default RecoveryDialogReason;